import { MdDeleteOutline } from 'react-icons/md';
import TextEdit from '../../../../molecules/TextEdit';
import SelectTextField from '../../../../molecules/SelectTextField';
import MessageCompositionPicker from './MessageCompositionPicker';
import type { CompositionOption } from '../useCollectionCompositions';
import {
    ACCOUNT_KIND_LABEL,
    CHANNEL_LABEL,
    CollectionChannel,
    CollectionPhase,
    CollectionRule,
    OBLIGOR_LABEL,
    ObligorRole,
    optionsOf,
    PAYMENT_LABEL,
    PaymentInstruction,
    ReceivingAccountKind,
} from '../collectionModel';

interface CollectionRuleRowProps {
    rule: CollectionRule;
    options: CompositionOption[];
    onChange: (rule: CollectionRule) => void;
    onRemove: () => void;
}

const ASSIGNOR_OPTIONS = [{ value: '1', label: '1 cedente' }, { value: '2', label: '2 cedentes' }];
const ANY_LABEL = 'Qualquer';
const MAX_DAYS_PAST_DUE = 365;

export const ruleGridClass = (phase: CollectionPhase) => (phase === 'OVERDUE'
    ? 'grid grid-cols-[100px_repeat(3,minmax(110px,1fr))_minmax(120px,1fr)_minmax(150px,1.2fr)_minmax(240px,2.2fr)_36px] gap-2 items-center'
    : 'grid grid-cols-[repeat(3,minmax(110px,1fr))_minmax(120px,1fr)_minmax(150px,1.2fr)_minmax(240px,2.2fr)_36px] gap-2 items-center');

export const RuleGridHeader = ({ phase }: { phase: CollectionPhase }) => (
    <div className={`${ruleGridClass(phase)} px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500`}>
        {phase === 'OVERDUE' && <span>Atraso desde (dias)</span>}
        <span>Cedentes</span>
        <span>Conta</span>
        <span>Como paga</span>
        <span>Canal</span>
        <span>Para quem</span>
        <span>Composição da mensagem</span>
        <span />
    </div>
);

const fieldId = (rule: CollectionRule, field: string) => `${rule.id}-${field}`;

const CollectionRuleRow = ({ rule, options, onChange, onRemove }: CollectionRuleRowProps) => {
    const setCriteria = (patch: Partial<CollectionRule['criteria']>) =>
        onChange({ ...rule, criteria: { ...rule.criteria, ...patch } });

    return (
        <div className={`${ruleGridClass(rule.phase)} rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/40 ${rule.skip ? 'opacity-70' : ''}`}>
            {rule.phase === 'OVERDUE' && (
                <TextEdit id={fieldId(rule, 'fromDaysPastDue')} label="" type="number" min={1} max={MAX_DAYS_PAST_DUE}
                    aria-label="Atraso desde (dias)" value={rule.criteria.fromDaysPastDue ?? ''}
                    onChange={e => setCriteria({ fromDaysPastDue: e.target.value === '' ? null : Number(e.target.value) })} />
            )}
            <SelectTextField id={fieldId(rule, 'assignorCount')} label="" items={ASSIGNOR_OPTIONS} emptyLabel={ANY_LABEL}
                value={rule.criteria.assignorCount ? String(rule.criteria.assignorCount) : ''}
                onChange={e => setCriteria({ assignorCount: e.target.value ? (Number(e.target.value) as 1 | 2) : null })} />
            <SelectTextField id={fieldId(rule, 'receivingAccountKind')} label="" items={optionsOf(ACCOUNT_KIND_LABEL)} emptyLabel={ANY_LABEL}
                value={rule.criteria.receivingAccountKind ?? ''}
                onChange={e => setCriteria({ receivingAccountKind: (e.target.value || null) as ReceivingAccountKind | null })} />
            <SelectTextField id={fieldId(rule, 'paymentInstruction')} label="" items={optionsOf(PAYMENT_LABEL)} emptyLabel={ANY_LABEL}
                value={rule.criteria.paymentInstruction ?? ''}
                onChange={e => setCriteria({ paymentInstruction: (e.target.value || null) as PaymentInstruction | null })} />
            <SelectTextField id={fieldId(rule, 'channel')} label="" items={optionsOf(CHANNEL_LABEL)} hideEmptyOption value={rule.channel}
                onChange={e => onChange({ ...rule, channel: (e.target.value || 'EMAIL') as CollectionChannel, messageCompositionId: null })} />
            <SelectTextField id={fieldId(rule, 'obligorRole')} label="" items={optionsOf(OBLIGOR_LABEL)} hideEmptyOption value={rule.obligorRole}
                onChange={e => onChange({ ...rule, obligorRole: (e.target.value || 'PAYER') as ObligorRole })} />
            <MessageCompositionPicker channel={rule.channel} options={options} value={rule}
                onChange={choice => onChange({ ...rule, ...choice })} />
            <button type="button" onClick={onRemove} aria-label="Remover regra" title="Remover regra"
                className="h-9 w-9 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-slate-700">
                <MdDeleteOutline size={18} />
            </button>
        </div>
    );
};

export default CollectionRuleRow;
